import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { IExchangeModel } from 'src/app/logic/exchanges/model/exchange.model';
import { ExchangeRateCalculatorService } from 'src/app/services/exchange-rate-calculator.service';

@Component({
  selector: 'app-quoter',
  templateUrl: './quoter.component.html',
  styleUrls: ['./quoter.component.scss'],
})
export class QuoterComponent implements OnInit, OnChanges {
  @Input() queDivisaTienes!: IExchangeModel;
  @Input() queDivisaQuieres!: IExchangeModel;
  @Input() divisas!: IExchangeModel[];
  @Input() sucursal: any;
  @Input() quotes: any;
  @Input() promoLocked = false;
  @Input() disabled = false;

  @Output() swapCurrencies = new EventEmitter<void>();

  @Output() changeCurrency = new EventEmitter<{
    currency: IExchangeModel;
    type: 'from' | 'to';
  }>();

  @Output() amountChange = new EventEmitter<{
    from: IExchangeModel;
    to: IExchangeModel;
    amount: number;
    type: string;
  }>();

  @Output() branchChange = new EventEmitter<any>();

  public fromAmount: number = 0;
  public toAmount: number = 0;
  public availableFrom: IExchangeModel[] = [];
  public availableTo: IExchangeModel[] = [];
  public selectedBranch: any = null;

  public readonly cenOptions = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

  private isRecalculating = false;

  get isFromCen(): boolean {
    return this.queDivisaTienes?.currency?.code === 'CEN';
  }

  get isToCen(): boolean {
    return this.queDivisaQuieres?.currency?.code === 'CEN';
  }

  constructor(
    private readonly exchangeRateCalculator: ExchangeRateCalculatorService,
  ) {}

  ngOnInit(): void {
    this.selectedBranch = this.sucursal;
    this.applyDefaultCurrenciesIfNeeded();
    this.initCurrencyLists();
    this.hydrateFromQuotes(false);
  }

  ngOnChanges(changes: SimpleChanges): void {
    const quotesChange = changes['quotes'];
    if (quotesChange && this.quotes?.from && this.quotes?.to) {
      this.hydrateFromQuotes(false);
    }

    const fromChange = changes['queDivisaTienes'];
    const toChange = changes['queDivisaQuieres'];
    const hasRatesChange =
      (fromChange && !fromChange.firstChange) ||
      (toChange && !toChange.firstChange);

    if (hasRatesChange && this.hasDistinctCurrencies()) {
      this.initCurrencyLists();
      if (this.fromAmount > 0) {
        this.recalculateAndEmit('from');
      } else if (this.toAmount > 0) {
        this.recalculateAndEmit('to');
      }
    }
  }

  initCurrencyLists(): void {
    this.availableFrom = this.divisas.filter(
      (d) => d.id !== this.queDivisaQuieres.id,
    );
    this.availableTo = this.divisas.filter(
      (d) => d.id !== this.queDivisaTienes.id,
    );
  }

  onSwapCurrencies(): void {
    if (this.promoLocked) {
      return;
    }
    const temp = this.queDivisaTienes;
    this.queDivisaTienes = this.queDivisaQuieres;
    this.queDivisaQuieres = temp;

    const tempAmount = this.fromAmount;
    this.fromAmount = this.toAmount;
    this.toAmount = tempAmount;

    this.initCurrencyLists();
    if (this.isFromCen) {
      this.fromAmount = this.normalizeCenUnits(this.fromAmount);
      this.recalculateAndEmit('from');
    } else if (this.isToCen) {
      this.toAmount = this.normalizeCenUnits(this.toAmount);
      this.recalculateAndEmit('to');
    } else if (this.fromAmount > 0) {
      this.recalculateAndEmit('from');
    } else if (this.toAmount > 0) {
      this.recalculateAndEmit('to');
    } else {
      this.emitQuoteState('from');
    }
    this.swapCurrencies.emit();
  }

  onChangeCurrency(currency: IExchangeModel, type: 'from' | 'to'): void {
    if (this.promoLocked) {
      return;
    }
    if (type === 'from') {
      this.queDivisaTienes = currency;
    } else {
      this.queDivisaQuieres = currency;
    }
    this.initCurrencyLists();
    this.changeCurrency.emit({ currency, type });

    if (currency.currency?.code === 'CEN') {
      if (type === 'from') {
        this.fromAmount = 1;
      } else {
        this.toAmount = 1;
      }
    }

    this.onAmountChange(type);
  }

  onAmountChange(type: 'from' | 'to'): void {
    if (this.isRecalculating) return;
    this.recalculateAndEmit(type);
  }

  private applyDefaultCurrenciesIfNeeded(): void {
    if (this.quotes?.from) {
      const fromExchange = this.resolveExchangeModel(this.quotes.from);
      if (fromExchange) {
        this.queDivisaTienes = fromExchange;
      }
    }

    if (this.quotes?.to) {
      const toExchange = this.resolveExchangeModel(this.quotes.to);
      if (toExchange) {
        this.queDivisaQuieres = toExchange;
      }
    }

    if (!this.queDivisaTienes?.currency?.id && this.divisas?.length) {
      const mxn = this.divisas.find((d) => d.currency?.code === 'MXN');
      if (mxn) {
        this.queDivisaTienes = mxn;
      }
    }
  }

  private hydrateFromQuotes(emit: boolean): void {
    if (!this.quotes?.from || !this.quotes?.to) {
      return;
    }

    const fromExchange = this.resolveExchangeModel(this.quotes.from);
    const toExchange = this.resolveExchangeModel(this.quotes.to);
    if (fromExchange) {
      this.queDivisaTienes = fromExchange;
    }
    if (toExchange) {
      this.queDivisaQuieres = toExchange;
    }

    this.initCurrencyLists();

    if (!this.hasDistinctCurrencies()) {
      return;
    }

    this.fromAmount = this.exchangeRateCalculator.getFromAmountForQuote(
      this.quotes,
    );
    this.toAmount = this.exchangeRateCalculator.getToAmountForQuote(this.quotes);

    if (emit) {
      const type = this.quotes.type === 'to' ? 'to' : 'from';
      this.emitQuoteState(type);
    }
  }

  private resolveExchangeModel(
    exchange: IExchangeModel,
  ): IExchangeModel | undefined {
    if (!exchange) {
      return undefined;
    }
    if (this.promoLocked || !this.divisas?.length) {
      return exchange;
    }
    return (
      this.divisas.find((d) => d.id === exchange.id) ??
      this.divisas.find(
        (d) => d.currency?.id === exchange.currency?.id,
      ) ??
      exchange
    );
  }

  private hasDistinctCurrencies(): boolean {
    const fromId = this.queDivisaTienes?.currency?.id;
    const toId = this.queDivisaQuieres?.currency?.id;
    return !!fromId && !!toId && fromId !== toId;
  }

  private normalizeCenUnits(amount: number): number {
    return Math.min(10, Math.max(1, Math.round(Number(amount) || 0)));
  }

  private recalculateAndEmit(type: 'from' | 'to'): void {
    if (!this.queDivisaTienes || !this.queDivisaQuieres) {
      return;
    }

    if (!this.hasDistinctCurrencies()) {
      return;
    }

    this.isRecalculating = true;
    if (type === 'from') {
      const amount = Number(this.fromAmount) || 0;
      this.toAmount = this.exchangeRateCalculator.convertFromAmount(
        this.queDivisaTienes,
        this.queDivisaQuieres,
        amount,
      );
    } else {
      const amount = Number(this.toAmount) || 0;
      this.fromAmount = this.exchangeRateCalculator.convertToAmount(
        this.queDivisaTienes,
        this.queDivisaQuieres,
        amount,
      );
    }

    if (this.isToCen) {
      this.toAmount = Math.min(10, Math.max(1, Math.round(this.toAmount)));
      this.fromAmount = this.exchangeRateCalculator.convertToAmount(
        this.queDivisaTienes,
        this.queDivisaQuieres,
        this.toAmount,
      );
    } else if (this.isFromCen) {
      this.fromAmount = Math.min(10, Math.max(1, Math.round(this.fromAmount)));
      this.toAmount = this.exchangeRateCalculator.convertFromAmount(
        this.queDivisaTienes,
        this.queDivisaQuieres,
        this.fromAmount,
      );
    }

    this.isRecalculating = false;
    this.emitQuoteState(type);
  }

  private emitQuoteState(type: 'from' | 'to'): void {
    if (!this.hasDistinctCurrencies()) {
      return;
    }

    this.amountChange.emit({
      from: this.queDivisaTienes,
      to: this.queDivisaQuieres,
      amount: type === 'from' ? Number(this.fromAmount) : Number(this.toAmount),
      type,
    });
  }

  onBranchChange(branch: any): void {
    this.selectedBranch = branch;
    this.branchChange.emit(branch);
  }
}
